import { useRef } from "react";
import { useT } from "../i18n/hooks.ts";

/**
 * File picker trigger styled like every other button in the app (a hidden
 * native `<input type="file">`, clicked via ref, behind a real `.button`) —
 * a plain `<input type="file">` shows the browser's OWN button text, at the
 * mercy of the browser/OS locale rather than this app's own language
 * setting (seen for real: "Choose File" inside an all-Spanish UI). This
 * keeps every upload button's label following `language` like the rest of
 * the app, and gives it the app's own look instead of the OS-themed native
 * control.
 */
export function FileButton({
  accept,
  multiple,
  onFiles,
  disabled,
  label,
}: {
  accept?: string;
  multiple?: boolean;
  onFiles: (files: FileList | null) => void;
  disabled?: boolean;
  /** Overrides the default "Choose file(s)" text — for callers that need something more specific (e.g. Landing.tsx's "Import copy…"). */
  label?: string;
}) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        className="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        {label ?? t(multiple ? "common.chooseFiles" : "common.chooseFile")}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(e) => {
          onFiles(e.target.files);
          e.target.value = "";
        }}
        style={{ display: "none" }}
      />
    </>
  );
}
